import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, ArrowRight, User, X } from 'lucide-react';

const blogPosts = [
  {
    id: 1,
    title: "5 Signs Your Business Needs an AI Transformation",
    excerpt: "Artificial Intelligence isn't just a buzzword for tech giants anymore; it is a fundamental shift in how efficient businesses operate.",
    category: "AI Strategy",
    author: "Don McCann",
    date: "January 2026",
    readTime: "5 min read",
    image: "https://customer-assets.emergentagent.com/job_aifirst-sme/artifacts/guhofrt0_Gemini_Generated_Image_vrp55uvrp55uvrp5.png",
    content: `Artificial Intelligence isn't just a buzzword for tech giants anymore; it is a fundamental shift in how efficient businesses operate. If you are wondering whether it is time to invest, here are the five red flags that suggest your operations are due for a modern upgrade.

**1. Your Team is Drowning in "Busy Work"**
If your high-value employees spend hours on data entry, scheduling, or sorting emails, you are wasting talent. AI excels at repetitive, rules-based tasks. Automating these frees your team to focus on strategy and creativity.

**2. You Have Data, But No Insights**
Most businesses hoard data but rarely use it effectively. If you cannot answer simple questions about your sales trends or customer behaviour without a week-long manual audit, you need AI analytics to turn raw numbers into real-time decision-making power.

**3. Customer Response Times are Slipping**
In an on-demand world, waiting 24 hours for an email reply is unacceptable. If your support queue is unmanageable, an AI-driven chatbot or automated triage system can handle the FAQs, leaving the complex issues for your human agents.

**4. You Can't Scale Without Hiring**
If growing your revenue requires a linear increase in headcount, your margins will suffer. AI allows you to scale operations—processing more orders, queries, and data—without immediately needing to double your workforce.

**5. Your Competitors are Moving Faster**
If it feels like the competition is launching features faster or predicting market trends before you do, they are likely using AI to speed up their R&D and market analysis.

**The Bottom Line:** AI transformation isn't about replacing people; it's about removing friction. If these signs sound familiar, it is time to start your strategy.`,
  },
  {
    id: 2,
    title: "How SMEs Can Compete with Enterprise Using AI Tools",
    excerpt: "Historically, large enterprises won because they had the biggest budgets. Today, the playing field is levelling.",
    category: "Business Growth",
    author: "Caroline Halpin",
    date: "January 2026",
    readTime: "7 min read",
    image: "https://customer-assets.emergentagent.com/job_aifirst-sme/artifacts/a7ek88vb_Gemini_Generated_Image_le5povle5povle5p.png",
    content: `Historically, large enterprises won because they had the biggest budgets. Today, the playing field is levelling. Small and Medium Enterprises (SMEs) actually have a distinct advantage: agility. By leveraging accessible AI tools, SMEs can punch well above their weight.

**1. Personalisation at Scale**
You don't need a massive marketing department to treat every customer like a VIP. Generative AI tools can help you craft hyper-personalised emails, product recommendations, and content strategies that rival global brands.

**2. 24/7 Operations on a 9-5 Budget**
Enterprises have call centres; you have AI agents. Implementing smart chatbots ensures you capture leads and assist customers even when your office is closed, ensuring you never miss an opportunity due to time zones or holidays.

**3. Predictive Analytics for Everyone**
Previously, forecasting required a team of data scientists. Now, AI-integrated platforms (like modern CRMs) can analyse your sales pipeline and predict cash flow or inventory needs with frightening accuracy, helping you manage resources as tightly as a CFO.

**4. Content Creation Velocity**
Consistency is key in digital marketing. AI writing assistants and design tools allow a one-person marketing team to produce blogs, social posts, and visuals at a speed that used to require an agency.

**Summary:** The barrier to entry has lowered. The question is no longer if you can afford AI tools, but whether you can afford to ignore them while your agile competitors race ahead.`,
  },
  {
    id: 3,
    title: "The Complete Guide to Google Workspace Migration",
    excerpt: "Moving from legacy systems to a unified cloud environment like Google Workspace is one of the best moves you can make for collaboration.",
    category: "Cloud Migration",
    author: "Don McCann",
    date: "January 2026",
    readTime: "10 min read",
    image: "https://customer-assets.emergentagent.com/job_aifirst-sme/artifacts/it5fincn_Gemini_Generated_Image_nemhi7nemhi7nemh.png",
    content: `Moving from legacy systems (or disjointed software) to a unified cloud environment like Google Workspace is one of the best moves you can make for collaboration. However, the migration process can be daunting. Here is the roadmap to a seamless transition.

**Phase 1: The Audit**
Before you move a single file, map out your current landscape. How much data do you have? Which email accounts are active? Do you have shared calendars or legacy archives? You cannot migrate what you haven't measured.

**Phase 2: The Setup & Validation**
Set up your Google Workspace environment before switching over. Verify your domain (DNS records), create your user accounts, and set up security groups. Crucial Step: Run a test migration with a small pilot group to catch any snagging issues early.

**Phase 3: The Data Migration**
This is the heavy lifting. Use Google's migration tools to transfer emails, contacts, and calendar data.

**Tip:** Migrate historical email data in the background a week prior, then do a final "delta" sync on the go-live weekend to catch recent items.

**Phase 4: Change Management**
Technology is easy; people are hard. If your team doesn't know how to use Google Drive or Meet, the migration fails. Run training sessions before the switch. Show them the benefits—like real-time document collaboration—so they are excited, not frustrated.

**Phase 5: Go Live**
Flip the MX records (directing email to Google) during off-hours (Friday evening is standard). By Monday morning, your team logs into a fresh, modern workspace, ready to collaborate.`,
  },
];

const BlogModal = ({ post, onClose }) => {
  if (!post) return null;

  const renderContent = (content) => {
    return content.split('\n\n').map((paragraph, index) => {
      if (paragraph.startsWith('**') && paragraph.includes('**')) {
        const parts = paragraph.split('**');
        return (
          <p key={index} className="mb-4">
            {parts.map((part, i) => 
              i % 2 === 1 ? <strong key={i} className="text-[var(--text-primary)]">{part}</strong> : part
            )}
          </p>
        );
      }
      return <p key={index} className="mb-4">{paragraph}</p>;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Image */}
        <div className="relative h-64 overflow-hidden">
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
          >
            <X className="w-5 h-5 text-[var(--text-primary)]" />
          </button>
          <div className="absolute bottom-4 left-6 right-6">
            <span className="px-3 py-1 rounded-full bg-[var(--accent-primary)] text-xs font-medium text-[var(--text-primary)] mb-3 inline-block">
              {post.category}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              {post.title}
            </h2>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 md:p-8 overflow-y-auto max-h-[calc(90vh-16rem)]">
          {/* Meta */}
          <div className="flex items-center gap-4 text-sm text-[var(--text-muted)] mb-6 pb-6 border-b border-black/10">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{post.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{post.readTime}</span>
            </div>
          </div>

          {/* Article Content */}
          <div className="prose prose-lg text-[var(--text-secondary)] leading-relaxed">
            {renderContent(post.content)}
          </div>

          {/* CTA */}
          <div className="mt-8 pt-6 border-t border-black/10">
            <p className="text-[var(--text-secondary)] mb-4">
              Ready to transform your business? Let's talk about how we can help.
            </p>
            <a href="#contact" onClick={onClose} className="btn-primary inline-flex">
              Book a Strategy Call
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Blog = () => {
  const [selectedPost, setSelectedPost] = useState(null);

  return (
    <>
      <section id="blog" className="py-20 md:py-32 bg-[var(--bg-section)]">
        <div className="container">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-[var(--accent-wash)] text-[var(--accent-text)] text-sm font-medium mb-4">
              Insights & Resources
            </span>
            <h2 className="heading-2 mb-4">
              Latest from Our Blog
            </h2>
            <p className="body-large">
              Expert insights on AI transformation, digital strategy, and business growth for SMEs.
            </p>
          </motion.div>

          {/* Blog Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white rounded-2xl overflow-hidden border border-black/10 hover:border-[var(--accent-primary)] hover:shadow-lg transition-all duration-300 cursor-pointer"
                onClick={() => setSelectedPost(post)}
              >
                {/* Image */}
                <div className="aspect-[16/10] overflow-hidden relative">
                  <img 
                    src={post.image} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 rounded-full bg-white/90 backdrop-blur-sm text-xs font-medium text-[var(--text-primary)]">
                      {post.category}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Meta */}
                  <div className="flex items-center gap-4 text-xs text-[var(--text-muted)] mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>

                  {/* Title */}
                  <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 group-hover:text-[var(--accent-text)] transition-colors line-clamp-2">
                    {post.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-sm text-[var(--text-secondary)] mb-4 line-clamp-3">
                    {post.excerpt}
                  </p>

                  {/* Author & CTA */}
                  <div className="flex items-center justify-between pt-4 border-t border-black/5">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[var(--accent-wash)] flex items-center justify-center">
                        <User className="w-4 h-4 text-[var(--accent-text)]" />
                      </div>
                      <span className="text-sm font-medium text-[var(--text-secondary)]">
                        {post.author}
                      </span>
                    </div>
                    <span className="flex items-center gap-1 text-sm font-medium text-[var(--accent-text)] group-hover:gap-2 transition-all">
                      Read More
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Subscribe Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-12"
          >
            <p className="text-[var(--text-secondary)] mb-4">
              Want more insights? Subscribe to be notified when new articles are published.
            </p>
            <a href="#contact" className="btn-primary inline-flex">
              Get Notified
            </a>
          </motion.div>
        </div>
      </section>

      {/* Blog Modal */}
      <AnimatePresence>
        {selectedPost && (
          <BlogModal post={selectedPost} onClose={() => setSelectedPost(null)} />
        )}
      </AnimatePresence>
    </>
  );
};

export default Blog;
